import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Grid,
  TextField,
  Autocomplete,
  Chip,
} from '@mui/material';
import { FiPlus, FiChevronLeft, FiChevronRight, FiSearch, FiX, FiTrash2 } from 'react-icons/fi';
import { GrDocumentDownload } from 'react-icons/gr';

import { Sort, ArrowUpward, ArrowDownward } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { transactionsAPI } from '../services/api';
import { colors } from '../theme/modernTheme';
import { showSuccess, showError } from '../utils/toast';
import { formatDateForDisplay } from '../utils/dateUtils';
import TableLoader from '../components/TableLoader';
import BulkDeleteDialog from '../components/BulkDeleteDialog';
import { useLoading } from '../context/LoadingContext';
import { useAuth } from '../context/AuthContext';
import { adjustPageAfterDeletion } from '../utils/paginationUtils';
import {
  fetchReceiptPreview,
  downloadTransactionReceipt,
  sendTransactionReceiptEmail,
  sendTransactionReceiptWhatsApp,
} from '../utils/receiptHelpers';
import { selectDonationSchemas } from '../store/slices/donationSchemasSlice';
import ReceiptPreviewModal from '../components/ReceiptPreviewModal';
import { paymentMethodLabel } from '../utils/paymentMethodLabel';

export default function Transaction() {
  const { user } = useAuth();
  const { showLoading, hideLoading } = useLoading();
  const navigate = useNavigate();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);

  // Get donation schemas from Redux
  const donationSchemas = useSelector(selectDonationSchemas);

  const canDelete = user?.role === 'ADMIN';

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(50);
  const [totalCount, setTotalCount] = useState(0);

  // Sorting
  const [orderBy, setOrderBy] = useState('payment_date');
  const [order, setOrder] = useState('desc');

  // Delete state
  const [singleDeleteDialogOpen, setSingleDeleteDialogOpen] = useState(false);
  const [transactionToDelete, setTransactionToDelete] = useState(null);

  // Receipt preview state
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewHTML, setPreviewHTML] = useState('');
  const [previewTransaction, setPreviewTransaction] = useState(null);

  // Temporary filter states (not applied until user clicks Apply)
  const [searchInput, setSearchInput] = useState('');
  const [donationSchemaInput, setDonationSchemaInput] = useState([]);
  const [startDateInput, setStartDateInput] = useState('');
  const [endDateInput, setEndDateInput] = useState('');

  // Applied filter states (used for API calls)
  const [search, setSearch] = useState('');
  const [donationSchemaFilter, setDonationSchemaFilter] = useState([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  useEffect(() => {
    loadTransactions();
  }, [page, donationSchemaFilter, startDate, endDate, search, orderBy, order]);

  const loadTransactions = async () => {
    setLoading(true);
    try {
      const params = {
        page: page + 1,
        limit: rowsPerPage,
        sortBy: orderBy,
        sortOrder: order,
      };

      if (donationSchemaFilter.length > 0) {
        params.donation_schema = donationSchemaFilter.map((s) => s.id).join(',');
      }
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      if (search) params.search = search;

      const response = await transactionsAPI.getAll(params);
      setTransactions(response.data);
      setTotalCount(response.pagination?.total || response.data.length);
    } catch (error) {
      showError('Failed to load transactions');
    } finally {
      setLoading(false);
    }
  };

  const handleApplyFilters = () => {
    setSearch(searchInput);
    setDonationSchemaFilter(donationSchemaInput);
    setStartDate(startDateInput);
    setEndDate(endDateInput);
    setPage(0);
  };

  const handleClearFilters = () => {
    setSearchInput('');
    setDonationSchemaInput([]);
    setStartDateInput('');
    setEndDateInput('');
    setSearch('');
    setDonationSchemaFilter([]);
    setStartDate('');
    setEndDate('');
    setPage(0);
  };

  const handleSort = (column) => {
    if (orderBy === column) {
      // If already sorting by this column, toggle order
      setOrder(order === 'asc' ? 'desc' : 'asc');
    } else {
      // First click on a new column - payment_date defaults to desc
      setOrder('desc');
      setOrderBy(column);
    }
    setPage(0); // Reset to first page when sorting changes
  };

  const handleFilterKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleApplyFilters();
    }
  };

  const hasActiveFilters =
    searchInput || donationSchemaInput.length > 0 || startDateInput || endDateInput;

  // Check if any filter has changed from applied state
  const hasFilterChanges =
    searchInput !== search ||
    JSON.stringify(donationSchemaInput) !== JSON.stringify(donationSchemaFilter) ||
    startDateInput !== startDate ||
    endDateInput !== endDate;

  const handleAddTransaction = () => {
    navigate('/transactions/new');
  };

  const handleViewTransaction = (id) => {
    navigate(`/transactions/${id}`);
  };

  const handleSingleDelete = (transaction) => {
    setTransactionToDelete(transaction);
    setSingleDeleteDialogOpen(true);
  };

  const handleDownloadPDF = async (transaction) => {
    await downloadTransactionReceipt(transaction, {
      showLoading,
      hideLoading,
      showSuccess,
      showError,
    });
  };

  const handleSendEmail = async (transaction) => {
    if (!transaction.primary_email) return;
    await sendTransactionReceiptEmail(transaction, {
      showLoading,
      hideLoading,
      showSuccess,
      showError,
    });
  };

  const handleSendWhatsApp = async (transaction) => {
    if (!transaction.phone_1) return;
    await sendTransactionReceiptWhatsApp(transaction, {
      showLoading,
      hideLoading,
      showSuccess,
      showError,
    });
  };

  // Open receipt preview modal
  const handlePreviewReceipt = async (transaction) => {
    try {
      showLoading();
      const data = await fetchReceiptPreview(transaction.id);
      setPreviewHTML(data.html);
      setPreviewTransaction({
        ...transaction,
        ...data.transaction,
      });
      setPreviewOpen(true);
    } catch (error) {
      console.error('Preview error:', error);
      showError('Failed to load receipt preview');
    } finally {
      hideLoading();
    }
  };

  const handlePreviewClose = () => {
    setPreviewOpen(false);
    setPreviewHTML('');
    setPreviewTransaction(null);
  };

  const handleSingleDeleteConfirm = async () => {
    try {
      showLoading();
      await transactionsAPI.delete(transactionToDelete.id);

      showSuccess('Transaction deleted successfully');

      setSingleDeleteDialogOpen(false);
      setTransactionToDelete(null);

      const newPage = adjustPageAfterDeletion(page, 1, totalCount, rowsPerPage);
      if (newPage !== page) {
        setPage(newPage);
      } else {
        await loadTransactions();
      }
    } catch (error) {
      const errorMessage =
        error?.error?.message || error?.message || 'Failed to delete transaction';
      showError(errorMessage);
    } finally {
      hideLoading();
    }
  };

  const totalPages = Math.ceil(totalCount / rowsPerPage);

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.2} mt={1}>
        <Typography
          variant="h4"
          sx={{
            fontSize: '1.5rem',
            fontWeight: 700,
            color: colors.text.primary,
          }}
        >
          Transactions
        </Typography>
        <Button
          variant="contained"
          startIcon={<FiPlus />}
          onClick={handleAddTransaction}
          sx={{
            borderRadius: '4px',
            backgroundColor: colors.primary.main,
            '&:hover': { backgroundColor: colors.primary.dark },
          }}
        >
          Add Transaction
        </Button>
      </Box>

      {/* Filters and Pagination */}
      <Paper
        sx={{
          p: 2,
          mb: 3,
          borderRadius: '4px',
          boxShadow: 'none',
        }}
      >
        <Grid container spacing={2} alignItems="center">
          {/* Search */}
          <Grid item xs={12} md={2.5}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search donor..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyPress={handleFilterKeyPress}
              autoComplete="off"
              InputProps={{
                endAdornment: searchInput ? (
                  <IconButton
                    size="small"
                    onClick={() => setSearchInput('')}
                    sx={{ padding: '4px' }}
                  >
                    <FiX size={16} style={{ color: colors.text.secondary }} />
                  </IconButton>
                ) : (
                  <FiSearch style={{ marginRight: 8, color: colors.text.secondary }} />
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: colors.background.paper,
                  borderRadius: '4px',
                },
              }}
            />
          </Grid>

          {/* Donation Scheme Filter - Multi-select */}
          <Grid item xs={12} md={3.5}>
            <Autocomplete
              multiple
              size="small"
              options={donationSchemas}
              getOptionLabel={(option) => option.name}
              value={donationSchemaInput}
              onChange={(event, newValue) => {
                setDonationSchemaInput(newValue);
                // Auto-apply when clearing all donation schemes (clicking X to clear all)
                if (newValue.length === 0 && donationSchemaInput.length > 0) {
                  setTimeout(() => {
                    setDonationSchemaFilter([]);
                    setPage(0);
                  }, 0);
                }
              }}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              disableCloseOnSelect
              forcePopupIcon={true}
              slotProps={{
                paper: {
                  sx: {
                    borderRadius: '4px',
                    maxHeight: '300px',
                    '& .MuiAutocomplete-listbox': {
                      padding: '4px',
                      maxHeight: '300px',
                    },
                  },
                },
              }}
              componentsProps={{
                popper: {
                  placement: 'bottom-start',
                  modifiers: [
                    {
                      name: 'flip',
                      enabled: false,
                    },
                  ],
                },
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Donation Schemes"
                  placeholder={donationSchemaInput.length === 0 ? 'All Schemes' : ''}
                  inputProps={{
                    ...params.inputProps,
                  }}
                  InputProps={{
                    ...params.InputProps,
                    style: { fontSize: '0.875rem' },
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '4px',
                    },
                  }}
                />
              )}
              renderTags={(value, getTagProps) => {
                const numTags = value.length;
                const limitTags = 1;

                return (
                  <>
                    {value.slice(0, limitTags).map((option, index) => {
                      const { key, ...chipProps } = getTagProps({ index });
                      return (
                        <Chip
                          key={key}
                          {...chipProps}
                          label={option.name}
                          size="small"
                          sx={{
                            borderRadius: '4px',
                            height: '20px',
                            fontSize: '0.9rem',
                            fontWeight: 500,
                            backgroundColor: colors.primary.main,
                            color: '#fff',
                            maxWidth: '120px',
                            '& .MuiChip-label': {
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            },
                            '& .MuiChip-deleteIcon': {
                              color: 'rgba(255, 255, 255, 0.7)',
                              '&:hover': {
                                color: '#fff',
                              },
                            },
                          }}
                        />
                      );
                    })}
                    {numTags > limitTags && (
                      <Box
                        component="span"
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          height: '18px',
                          px: 1.5,
                          borderRadius: '4px',
                          backgroundColor: `${colors.primary.main}15`,
                          color: colors.primary.main,
                          fontSize: '0.8rem',
                          fontWeight: 600,
                          ml: 0.5,
                        }}
                      >
                        +{numTags - limitTags}
                      </Box>
                    )}
                  </>
                );
              }}
              renderOption={(props, option, { selected }) => {
                const { key, ...optionProps } = props;
                return (
                  <Box
                    component="li"
                    key={key}
                    {...optionProps}
                    sx={{
                      display: 'flex !important',
                      alignItems: 'center',
                      gap: 1.5,
                      px: 1.5,
                      py: 1,
                      minHeight: '40px',
                      backgroundColor: 'transparent !important',
                      borderRadius: '4px !important',
                      '&:hover': {
                        backgroundColor: `${colors.action.hover} !important`,
                        borderRadius: '4px !important',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 18,
                        height: 18,
                        border: `2px solid ${selected ? colors.primary.main : '#64748b'}`,
                        borderRadius: '3px',
                        backgroundColor: selected ? colors.primary.main : 'transparent',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      {selected && (
                        <Box component="svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
                          <path
                            d="M10 3L4.5 8.5L2 6"
                            stroke="white"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </Box>
                      )}
                    </Box>
                    <Typography
                      sx={{
                        fontSize: '0.875rem',
                        fontWeight: 500,
                        color: colors.text.primary,
                      }}
                    >
                      {option.name}
                    </Typography>
                  </Box>
                );
              }}
            />
          </Grid>

          {/* Start Date */}
          <Grid item xs={12} md={1.5}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Start Date"
              value={startDateInput}
              onChange={(e) => setStartDateInput(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{
                onClick: (e) => e.target.showPicker?.(),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '4px',
                },
              }}
            />
          </Grid>

          {/* End Date */}
          <Grid item xs={12} md={1.5}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="End Date"
              value={endDateInput}
              onChange={(e) => setEndDateInput(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{
                onClick: (e) => e.target.showPicker?.(),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '4px',
                },
              }}
            />
          </Grid>

          {/* Filter Buttons */}
          <Grid item xs={12} md={1.5}>
            <Box display="flex" alignItems="center" gap={1}>
              <IconButton
                size="small"
                disableRipple
                onClick={handleApplyFilters}
                disabled={!hasFilterChanges}
                sx={{
                  backgroundColor: 'transparent',
                  color: colors.primary.main,
                  textTransform: 'none',
                  fontSize: '0.875rem',
                  fontWeight: 510,
                  p: '8px',
                  mr: -1,
                  '&:disabled': {
                    color: colors.text.disabled,
                  },
                  '&:hover': {
                    backgroundColor: 'transparent',
                  },
                }}
              >
                Apply
              </IconButton>
              <IconButton
                size="small"
                onClick={handleClearFilters}
                disabled={!hasActiveFilters}
                sx={{
                  backgroundColor: 'transparent',
                  color: colors.primary.main,
                  textTransform: 'none',
                  fontSize: '0.875rem',
                  fontWeight: 510,
                  p: '8px',
                  '&:disabled': {
                    color: colors.text.disabled,
                  },
                  '&:hover': {
                    backgroundColor: 'transparent',
                  },
                }}
              >
                Clear
              </IconButton>
            </Box>
          </Grid>

          {/* Pagination */}
          <Grid item xs={12} md={1.5}>
            <Box display="flex" alignItems="center" justifyContent="flex-end" gap={1}>
              <IconButton
                size="small"
                onClick={() => setPage(Math.max(0, page - 1))}
                disabled={page === 0}
                sx={{
                  borderRadius: 1,
                  marginRight: -2,
                }}
              >
                <FiChevronLeft />
              </IconButton>
              <Typography
                variant="body2"
                sx={{
                  color: colors.text.secondary,
                  minWidth: '100px',
                  textAlign: 'center',
                }}
              >
                Page {page + 1} of {totalPages || 1}
              </Typography>
              <IconButton
                size="small"
                onClick={() => setPage(page + 1)}
                disabled={page >= totalPages - 1}
                sx={{
                  borderRadius: 1,
                  marginLeft: -2,
                }}
              >
                <FiChevronRight />
              </IconButton>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {loading ? (
        <TableLoader message="Loading transactions..." />
      ) : transactions.length === 0 ? (
        <Box
          sx={{
            textAlign: 'center',
            py: 8,
            color: colors.text.secondary,
          }}
        >
          <Typography variant="body1">No transactions found</Typography>
        </Box>
      ) : (
        <TableContainer
          component={Paper}
          sx={{
            boxShadow: 'none',
            border: `1px solid ${colors.border.main}`,
            borderRadius: '4px',
            overflowX: 'auto',
          }}
        >
          <Table>
            <TableHead sx={{ borderBottom: '1px solid #E5E7EB' }}>
              <TableRow>
                <TableCell
                  sx={{
                    width: '110px',
                    fontWeight: 600,
                    color: colors.text.primary,
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    whiteSpace: 'nowrap',
                    py: 1.5,
                  }}
                >
                  Transaction ID
                </TableCell>
                <TableCell
                  sx={{
                    width: '110px',
                    fontWeight: 600,
                    color: colors.text.primary,
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    whiteSpace: 'nowrap',
                    cursor: 'pointer',
                    py: 1.5,
                    '&:hover': {
                      backgroundColor: colors.action.hover,
                    },
                  }}
                  onClick={() => handleSort('payment_date')}
                >
                  <Box display="flex" alignItems="center" gap={0.5}>
                    Donation Date
                    {orderBy === 'payment_date' ? (
                      order === 'asc' ? (
                        <ArrowUpward fontSize="small" />
                      ) : (
                        <ArrowDownward fontSize="small" />
                      )
                    ) : (
                      <Sort fontSize="small" sx={{ opacity: 0.5 }} />
                    )}
                  </Box>
                </TableCell>
                <TableCell
                  sx={{
                    width: '200px',
                    fontWeight: 600,
                    color: colors.text.primary,
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    whiteSpace: 'nowrap',
                    py: 1.5,
                  }}
                >
                  Donor
                </TableCell>
                <TableCell
                  sx={{
                    width: '120px',
                    fontWeight: 600,
                    color: colors.text.primary,
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    whiteSpace: 'nowrap',
                    py: 1.5,
                  }}
                >
                  Donated Amount
                </TableCell>
                <TableCell
                  sx={{
                    width: '120px',
                    fontWeight: 600,
                    color: colors.text.primary,
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    whiteSpace: 'nowrap',
                    py: 1.5,
                  }}
                >
                  Donation Scheme
                </TableCell>
                <TableCell
                  sx={{
                    width: '150px',
                    fontWeight: 600,
                    color: colors.text.primary,
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    whiteSpace: 'nowrap',
                    py: 1.5,
                  }}
                >
                  Payment Method
                </TableCell>
                <TableCell
                  sx={{
                    width: '180px',
                    fontWeight: 600,
                    color: colors.text.primary,
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    textAlign: 'center',
                    whiteSpace: 'nowrap',
                    py: 1.5,
                  }}
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {transactions.map((transaction) => {
                return (
                  <TableRow
                    key={transaction.id}
                    sx={{
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: colors.action.row },
                      '&:not(:last-of-type)': {
                        borderBottom: `1px solid ${colors.border.main}`,
                      },
                    }}
                    onClick={() => handleViewTransaction(transaction.id)}
                  >
                    <TableCell
                      sx={{
                        width: '110px',
                        fontSize: '0.875rem',
                        color: colors.text.primary,
                        py: 1.5,
                      }}
                    >
                      #{transaction.id}
                    </TableCell>
                    <TableCell
                      sx={{
                        width: '110px',
                        fontSize: '0.875rem',
                        color: colors.text.primary,
                        py: 1.5,
                      }}
                    >
                      {formatDateForDisplay(transaction.payment_date)}
                    </TableCell>
                    <TableCell
                      sx={{
                        width: '200px',
                        fontSize: '0.875rem',
                        color: colors.text.primary,
                        py: 1.5,
                        maxWidth: '200px',
                      }}
                    >
                      <Tooltip
                        title={
                          transaction.first_name
                            ? `${transaction.first_name} ${transaction.last_name || ''}`.trim()
                            : transaction.organization_name
                        }
                        arrow
                        placement="top"
                      >
                        <Typography
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            fontSize: '0.875rem',
                            maxWidth: '200px',
                            display: 'block',
                          }}
                        >
                          {transaction.first_name
                            ? `${transaction.first_name} ${transaction.last_name || ''}`.trim()
                            : transaction.organization_name}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell
                      sx={{
                        width: '120px',
                        fontSize: '0.875rem',
                        color: colors.text.primary,
                        py: 1.5,
                      }}
                    >
                      ₹{parseFloat(transaction.amount).toLocaleString() || 0}
                    </TableCell>
                    <TableCell
                      sx={{
                        width: '150px',
                        fontSize: '0.875rem',
                        color: colors.text.primary,
                        py: 1.5,
                      }}
                    >
                      {transaction.donation_schema_name?.replace('_', ' ')}
                    </TableCell>
                    <TableCell
                      sx={{
                        width: '150px',
                        fontSize: '0.875rem',
                        color: colors.text.primary,
                        py: 1.5,
                      }}
                    >
                      {paymentMethodLabel(transaction.payment_method) || '--'}
                    </TableCell>
                    <TableCell sx={{ width: '200px', py: 1.5 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 0.5,
                        }}
                      >
                        <Tooltip title="Preview Receipt & Download Receipt" arrow>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handlePreviewReceipt(transaction);
                            }}
                            sx={{
                              color: colors.text.primary,
                              alignItems: 'center',
                              p: 1,
                              '&:hover': {
                                backgroundColor: 'transparent',
                                color: colors.primary.main,
                              },
                            }}
                          >
                            <GrDocumentDownload size={16} />
                          </IconButton>
                        </Tooltip>
                        {canDelete && (
                          <Tooltip title="Delete Transaction" arrow>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSingleDelete(transaction);
                              }}
                              sx={{
                                color: colors.text.primary,
                                p: 1,
                                '&:hover': {
                                  backgroundColor: 'transparent',
                                  color: colors.error.main,
                                },
                              }}
                            >
                              <FiTrash2 size={16} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Bottom Pagination */}
      {!loading && transactions.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            mt: 2,
            gap: 1,
          }}
        >
          <IconButton
            size="small"
            onClick={() => setPage(Math.max(0, page - 1))}
            disabled={page === 0}
            sx={{
              borderRadius: 1,
              marginRight: -2,
            }}
          >
            <FiChevronLeft />
          </IconButton>
          <Typography
            variant="body2"
            sx={{
              color: colors.text.secondary,
              minWidth: '100px',
              textAlign: 'center',
            }}
          >
            Page {page + 1} of {totalPages || 1}
          </Typography>
          <IconButton
            size="small"
            onClick={() => setPage(page + 1)}
            disabled={page >= totalPages - 1}
            sx={{
              borderRadius: 1,
              marginLeft: -2,
            }}
          >
            <FiChevronRight />
          </IconButton>
        </Box>
      )}

      {/* Single Delete Dialog */}
      <BulkDeleteDialog
        open={singleDeleteDialogOpen}
        onClose={() => {
          setSingleDeleteDialogOpen(false);
          setTransactionToDelete(null);
        }}
        onConfirm={handleSingleDeleteConfirm}
        selectedCount={1}
        itemName="transaction"
      >
        <Typography
          variant="body2"
          sx={{ color: colors.text.secondary, px: 2, textAlign: 'center' }}
        >
          Are you sure you want to proceed? This will undone the donation reminder status
        </Typography>
      </BulkDeleteDialog>

      {/* Receipt Preview Modal */}
      <ReceiptPreviewModal
        open={previewOpen}
        onClose={handlePreviewClose}
        content={previewHTML}
        hasEmail={!!previewTransaction?.primary_email}
        hasPhone={!!previewTransaction?.phone_1}
        donorName={previewTransaction?.donor_name || ''}
        onDownload={() => handleDownloadPDF(previewTransaction)}
        onSendEmail={() => handleSendEmail(previewTransaction)}
        onSendWhatsApp={() => handleSendWhatsApp(previewTransaction)}
        emailTemplate={previewTransaction?.email_template_id || ''}
        whatsappTemplate={previewTransaction?.whatsapp_template_id || ''}
      />
    </Box>
  );
}
